import { FC } from "react";
import CustomModal from "../../common/custom-modal/custom-modal";
import { Colors } from "../../../configs";
import { PaymentTypeEnum } from "../../../typings/enum/paymentTypes";
import PrintCopyButtons, { PrintCopyChoice, PrintFeedback } from "./PrintCopyButtons";

export type { PrintCopyChoice } from "./PrintCopyButtons";

interface ICashChangeModalProps {
    open: boolean;
    onClose: () => void;
    paymentType?: PaymentTypeEnum;
    billAmount?: number;
    tenderedAmount?: number;
    changeAmount?: number;
    isStoreCredit?: boolean;
    checkNumber?: string;
    outstandingAmount?: number;
    dueDate?: string;
    previousStoreCreditBalance?: number;
    customerName?: string;
    customerId?: number;
    invoiceId?: string;
    onPrintSelect?: (copy: PrintCopyChoice) => void;
    printingCopy?: PrintCopyChoice | null;
    printFeedback?: PrintFeedback | null;
}

function CheckIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[2rem] w-[2rem]" fill="none" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtDate = (iso?: string): string => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            month: "2-digit", day: "2-digit", year: "numeric",
        });
    } catch {
        return iso;
    }
};

interface TypeConfig {
    modalTitle: string;
    methodLabel: string;
    methodSubtitle: string;
    icon: string;
    accent: string;
    successColor: string;
    successLabel: string;
    isDeferred?: boolean;
}

function getConfig(
    paymentType: PaymentTypeEnum | undefined,
    customerName?: string,
    customerId?: number,
    checkNumber?: string,
    invoiceId?: string,
): TypeConfig {
    switch (paymentType) {
        case PaymentTypeEnum.CASH:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Paid with Cash",
                methodSubtitle: "Cash received from customer",
                icon: "💵", accent: "#7ABF7A",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
        case PaymentTypeEnum.CARD:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Paid with Card",
                methodSubtitle: "Card payment",
                icon: "💳", accent: "#7EADEB",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
        case PaymentTypeEnum.CHECK:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Paid with Check",
                methodSubtitle: checkNumber ? `Check #${checkNumber}` : "Check payment",
                icon: "🧾", accent: "#B596E2",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
        case PaymentTypeEnum.MONTHLY:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Monthly Account",
                methodSubtitle: customerName
                    ? `${customerName}${customerId ? ` • Account #${customerId}` : ""}`
                    : "Monthly account payment",
                icon: "👤", accent: "#7BCFD0",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
        case PaymentTypeEnum.LATER:
            return {
                modalTitle: "Payment Deferred",
                methodLabel: "Marked as Pay Later",
                methodSubtitle: invoiceId ? `Invoice #${invoiceId}` : "Deferred — to be collected later",
                icon: "📅", accent: "#F0B15B",
                successColor: "#D89216", successLabel: "Awaiting Payment",
                isDeferred: true,
            };
        case PaymentTypeEnum.STORE_CREDIT:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Store Credit",
                methodSubtitle: customerId ? `Store Credit #SC-${customerId}` : "Store credit applied",
                icon: "🎁", accent: "#F29AAD",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
        default:
            return {
                modalTitle: "Payment Complete",
                methodLabel: "Payment",
                methodSubtitle: "Payment received",
                icon: "💰", accent: "#7ABF7A",
                successColor: Colors.PRIMARY, successLabel: "Transaction Approved",
            };
    }
}

type DetailRowData = { left: string; right: string; divider?: boolean; strong?: boolean };

function getDetailRows(props: ICashChangeModalProps): DetailRowData[] {
    const { paymentType, billAmount, tenderedAmount, checkNumber,
            outstandingAmount, dueDate, previousStoreCreditBalance } = props;
    const bill = billAmount ?? 0;

    switch (paymentType) {
        case PaymentTypeEnum.CASH:
            return [
                { left: "Bill Total",    right: fmt(bill),                strong: true },
                { left: "Cash Received", right: fmt(tenderedAmount ?? 0), strong: true },
            ];
        case PaymentTypeEnum.CARD:
            return [
                { left: "Bill Total",   right: fmt(bill), strong: true },
                { left: "Card Charged", right: fmt(bill), strong: true },
            ];
        case PaymentTypeEnum.CHECK:
            return [
                { left: "Bill Total",   right: fmt(bill),          strong: true },
                { left: "Check Amount", right: fmt(bill),          strong: true },
                { left: "Check #",      right: checkNumber ?? "—", divider: true },
                { left: "Check Status", right: "Accepted" },
            ];
        case PaymentTypeEnum.MONTHLY: {
            const prev = outstandingAmount ?? 0;
            return [
                { left: "Previous Balance", right: fmt(prev),        strong: true },
                { left: "Current Charges",  right: fmt(bill),        strong: true },
                { left: "New Balance",      right: fmt(prev + bill), divider: true, strong: true },
            ];
        }
        case PaymentTypeEnum.LATER:
            return [
                { left: "Bill Total",        right: fmt(bill),        strong: true },
                { left: "Due Date",          right: fmtDate(dueDate), strong: true },
                { left: "Status",            right: "Scheduled",      divider: true },
                { left: "Collection Method", right: "To be collected later" },
            ];
        case PaymentTypeEnum.STORE_CREDIT: {
            const prev = previousStoreCreditBalance ?? 0;
            return [
                { left: "Store Credit Balance",     right: fmt(prev),                    strong: true },
                { left: "Bill Amount",              right: fmt(bill),                    strong: true },
                { left: "Applied Store Credit",     right: fmt(bill), divider: true,     strong: true },
                { left: "Remaining Credit Balance", right: fmt(Math.max(0, prev - bill)), strong: true },
            ];
        }
        default:
            return billAmount !== undefined
                ? [{ left: "Bill Total", right: fmt(bill), strong: true }]
                : [];
    }
}

function getHighlight(props: ICashChangeModalProps): { label: string; value: string; bg: string; text: string } | null {
    switch (props.paymentType) {
        case PaymentTypeEnum.CASH:
            return {
                label: props.isStoreCredit ? "Saved as Store Credit" : "Change Due",
                value: fmt(props.changeAmount ?? 0),
                bg: `${Colors.PRIMARY}14`,
                text: "#1B6B2A",
            };
        case PaymentTypeEnum.LATER:
            return {
                label: "Total Pay Later Balance",
                value: fmt((props.outstandingAmount ?? 0) + (props.billAmount ?? 0)),
                bg: "rgba(216,146,22,0.10)",
                text: "#7C5000",
            };
        default:
            return null;
    }
}

const CashChangeModal: FC<ICashChangeModalProps> = (props) => {
    const { open, onClose, paymentType, customerName, customerId, invoiceId, checkNumber,
            onPrintSelect, printingCopy, printFeedback } = props;

    const config = getConfig(paymentType, customerName, customerId, checkNumber, invoiceId);
    const rows = getDetailRows(props);
    const highlight = getHighlight(props);

    const handleClose = () => {
        onPrintSelect?.("none");
        onClose();
    };

    const handleDone = () => {
        onPrintSelect?.("none");
        onClose();
    };

    return (
        <CustomModal
            title={config.modalTitle}
            openModal={open}
            onClose={handleClose}
            width="38rem"
            height="auto"
        >
            <div className="flex flex-col pb-[0.25rem] pt-[0.25rem]" style={{ minHeight: "32rem" }}>

                {/* Success icon */}
                <div className="mb-[0.75rem] mt-[0.25rem] flex justify-center">
                    <div
                        className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: config.successColor }}
                    >
                        <CheckIcon />
                    </div>
                </div>

                {/* Success subtitle */}
                <div
                    className="mb-[1rem] text-center text-[1rem] font-bold"
                    style={{ color: config.successColor }}
                >
                    {config.successLabel}
                </div>

                {/* Payment method section */}
                <div className="mb-[1rem] flex items-center gap-[0.75rem]">
                    <div
                        className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full text-[1.625rem]"
                        style={{ backgroundColor: `${config.accent}33` }}
                    >
                        {config.icon}
                    </div>
                    <div>
                        <div
                            className="text-[0.875rem] font-bold leading-tight"
                            style={{ color: "#515151" }}
                        >
                            {config.methodLabel}
                        </div>
                        <div
                            className="mt-[0.125rem] text-[0.75rem]"
                            style={{ color: "rgba(81,81,81,0.75)" }}
                        >
                            {config.methodSubtitle}
                        </div>
                    </div>
                </div>

                {/* Detail panel */}
                {rows.length > 0 && (
                    <div
                        className="mb-[1rem] rounded-[0.75rem] p-[1rem]"
                        style={{ backgroundColor: "#F4F5F6" }}
                    >
                        {rows.map((row, idx) => (
                            <div
                                key={idx}
                                className={`flex items-center justify-between gap-4 py-[0.375rem] text-[0.8125rem] ${
                                    row.divider ? "mt-[0.25rem] border-t pt-[0.75rem]" : ""
                                }`}
                                style={row.divider ? { borderColor: "#D8DDE1" } : undefined}
                            >
                                <span style={{ color: "#515151" }}>{row.left}</span>
                                <span
                                    className={row.strong ? "font-bold" : "font-semibold"}
                                    style={{ color: "#515151" }}
                                >
                                    {row.right}
                                </span>
                            </div>
                        ))}

                        {highlight && (
                            <div
                                className="mt-[0.75rem] rounded-[0.625rem] px-[0.75rem] py-[0.75rem]"
                                style={{ backgroundColor: highlight.bg }}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-[0.875rem] font-bold" style={{ color: highlight.text }}>
                                        {highlight.label}
                                    </span>
                                    <span className="text-[1.0625rem] font-bold" style={{ color: highlight.text }}>
                                        {highlight.value}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Print Receipt */}
                {onPrintSelect && (
                    <PrintCopyButtons
                        onPrintSelect={onPrintSelect}
                        printingCopy={printingCopy}
                        printFeedback={printFeedback}
                    />
                )}

                {/* Done — closes the modal */}
                <button
                    type="button"
                    onClick={handleDone}
                    className="mt-auto h-[2.75rem] w-full rounded-[0.5625rem] text-[1rem] font-bold text-white shadow-sm transition hover:brightness-105"
                    style={{ backgroundColor: config.isDeferred ? "#D89216" : Colors.PRIMARY }}
                >
                    Done
                </button>
            </div>
        </CustomModal>
    );
};

export default CashChangeModal;
